import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import { ACCEPTED_FILE_TYPES } from 'config/accepted-file-types';
import { hiddenFromCarrierMap } from 'config/documents';
import { sortBy, isEqual } from 'lodash';
import CollapsibleItem from 'components/v2/list/CollapsibleItem';
import CircleButton from 'components/v2/buttons/CircleButton';
import CheckIcon from 'components/v2/icons/CheckIcon';
import Dropdown from 'components/v2/dropdowns/DropdownV2';
import LoadingCircle from 'components/ui/LoadingCircle';
import CargomaticDropzone from 'components/v2/DropZone';
import UploadedDocumentPreviewModal from 'components/modules/shipments/UploadedDocumentPreviewModal';
import { DocumentMagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { getShipperStopDocumentRequirementsFromConfig, getUniqueCombinedStopDocumentRequirementsFromConfig, } from 'helpers/stops';
import { getSignedUrls, waitDocumentUpload, getUniqueLatestDocumentsV2, } from 'utils/DocumentsAPIUtils';
import { showToast } from 'stores/ToastStore';
import SessionStore from 'stores/SessionStore';
import { useHandleUploadDocuments } from 'utils/DocumentsHooks';
import { getAllShipperDocumentRequirements, getAllShipperAndCarrierStopDocumentRequirements, } from 'utils/ShipperRulesAPIUtils';
import { featureEnabled, CONFIGURED_FEATURES } from 'helpers/feature-flags';
function Document({ document, onPreview, uploader }) {
    const { t } = useTranslation();
    const { completed } = document;
    function renderStatus() {
        if (completed) {
            return React.createElement(CheckIcon, { className: "text-green-cm w-5 h-5" });
        }
        return null;
    }
    function renderActions() {
        if (completed) {
            return (React.createElement("div", { "data-testid": "div-2d5db973", className: "flex items-center space-x-2" },
                React.createElement(CircleButton, { dataTestId: "CircleButton-f5e2f4d5", size: "small", onClick: onPreview },
                    React.createElement(DocumentMagnifyingGlassIcon, { className: "w-5 h-5", type: "solid" })),
                React.createElement(Dropdown, { dataTestId: "Dropdown-d41808dd", useIcon: true },
                    React.createElement(Dropdown.Item, { "data-testid": "undefined-9ca7ce9d", url: document === null || document === void 0 ? void 0 : document.url, target: "_blank" }, t('DOWNLOAD')),
                    React.createElement(Dropdown.Item, { "data-testid": "undefined-bbb7bd91" }, uploader()))));
        }
        return uploader(true);
    }
    return (React.createElement("div", { "data-testid": "div-d5623f65", className: "flex justify-between text-sm text-gray-700 py-1 items-center" },
        React.createElement("div", { "data-testid": "div-ba6a71ba", className: "flex items-center space-x-2" },
            React.createElement("span", { "data-testid": "span-2828a3eb" }, t(sluggify(document.name))),
            renderStatus()),
        renderActions()));
}
function usePreviousProps(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}
export default function MinimumDocumentRequirements({ shipment, title = 'MINIMUM_DOCUMENT_REQUIREMENTS', }) {
    const { t } = useTranslation();
    const isCarrierMandatoryStopDocumentsEnabled = featureEnabled(CONFIGURED_FEATURES.CARRIER_MANDATORY_STOP_DOCUMENTS);
    const isFromCarrierDashboard = title === 'PLEASE_PROVIDE_THE_FOLLOWING_DOCUMENTS';
    const isCarrier = SessionStore.isCarrier();
    const dropzoneRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [stopDocumentsConfig, setStopDocumentsConfig] = useState({});
    const [documents, setDocuments] = useState([]);
    const [uploadedDocuments, setUploadedDocuments] = useState([]);
    const [documentToPreview, setDocumentToPreview] = useState(null);
    const [documentsUploading, setDocumentsUploading] = useState({});
    const [carrierDocuments, setCarrierDocuments] = useState([]);
    const [shipperDocuments, setShipperDocuments] = useState([]);
    const keys = [
        shipment._id.toString(),
        ...shipment.stops.map((stop) => stop._id.toString()),
    ];
    const previous = usePreviousProps(shipment);
    useEffect(() => {
        if ((shipment === null || shipment === void 0 ? void 0 : shipment.shipper) && !isEqual(shipment, previous)) {
            getUploadedDocuments();
            getShipperStopDocumentRequirements();
        }
    }, [shipment]);
    useEffect(() => {
        if (shipment && Object.keys(stopDocumentsConfig).length) {
            getAllDocumentRequirements();
        }
    }, [uploadedDocuments, stopDocumentsConfig]);
    const getShipperStopDocumentRequirements = async () => {
        var _a;
        try {
            setLoading(true);
            const shipperId = ((_a = shipment === null || shipment === void 0 ? void 0 : shipment.shipper) === null || _a === void 0 ? void 0 : _a._id) || (shipment === null || shipment === void 0 ? void 0 : shipment.shipper);
            const config = isCarrierMandatoryStopDocumentsEnabled
                ? await getUniqueCombinedStopDocumentRequirementsFromConfig(shipperId, false)
                : await getShipperStopDocumentRequirementsFromConfig(shipperId);
            setStopDocumentsConfig(config);
        }
        catch (e) {
            showToast(t('ERROR_RETRIEVING_STOP_DOCUMENT_REQUIREMENTS'), null, 'error');
            console.error(`Error retrieving shipper stop documents config: ${e.message}`);
        }
    };
    async function getUploadedDocuments() {
        var _a, _b, _c, _d, _e, _f;
        try {
            const shipperId = (_b = (_a = shipment === null || shipment === void 0 ? void 0 : shipment.shipper) === null || _a === void 0 ? void 0 : _a._id) !== null && _b !== void 0 ? _b : shipment === null || shipment === void 0 ? void 0 : shipment.shipper;
            let documentRequirements = [];
            if (isCarrierMandatoryStopDocumentsEnabled) {
                const combinedConfig = await getUniqueCombinedStopDocumentRequirementsFromConfig(shipperId, false);
                const [combinedDocs] = await getAllShipperAndCarrierStopDocumentRequirements(shipment, combinedConfig);
                documentRequirements = combinedDocs;
            }
            else {
                const config = await getShipperStopDocumentRequirementsFromConfig(shipperId);
                documentRequirements = await getAllShipperDocumentRequirements(shipment, config);
            }
            const getDocumentsResult = await getUniqueLatestDocumentsV2(keys, documentRequirements);
            const uploadedDocuments = ((_d = (_c = getDocumentsResult === null || getDocumentsResult === void 0 ? void 0 : getDocumentsResult.data) === null || _c === void 0 ? void 0 : _c.getUniqueLatestDocumentsV2) === null || _d === void 0 ? void 0 : _d.documents) || [];
            const getSignedUrlsResult = await getSignedUrls(keys);
            const signedUrls = ((_f = (_e = getSignedUrlsResult === null || getSignedUrlsResult === void 0 ? void 0 : getSignedUrlsResult.data) === null || _e === void 0 ? void 0 : _e.getSignedUrls) === null || _f === void 0 ? void 0 : _f.signedUrls) || [];
            const documents = uploadedDocuments.map((doc) => {
                const url = signedUrls.find((url) => url.id === doc.id);
                return Object.assign(Object.assign({}, doc), { url: url === null || url === void 0 ? void 0 : url.signedUrl });
            });
            setUploadedDocuments(documents);
        }
        catch (e) {
            console.error(`Error retrieving uploaded documents: ${e.message}`);
        }
    }
    function mapDocumentUploadStatus(documentRequirements, excludeYardInterchange = false) {
        const sortedDocuments = sortBy(uploadedDocuments, (doc) => doc.createdAt).reverse();
        let docs = documentRequirements;
        if (excludeYardInterchange) {
            const isYardInterchangeUploaded = sortedDocuments.some(({ type }) => type === 'yard_interchange_ticket');
            if (!isYardInterchangeUploaded) {
                docs = documentRequirements.filter((docType) => docType !== 'yard_interchange_ticket');
            }
        }
        const documentsStatus = docs.map((type) => {
            const uploadedDocument = sortedDocuments.find((doc) => doc.type === type);
            return {
                name: type,
                url: uploadedDocument === null || uploadedDocument === void 0 ? void 0 : uploadedDocument.url,
                completed: !!uploadedDocument,
            };
        });
        return documentsStatus;
    }
    async function getAllDocumentRequirements() {
        try {
            let allDocuments = [];
            let carrierDocuments = [];
            let shipperDocuments = [];
            if (isCarrierMandatoryStopDocumentsEnabled) {
                const [combinedDocs, carrierStopDocs, allShipperDocs] = await getAllShipperAndCarrierStopDocumentRequirements(shipment, stopDocumentsConfig);
                allDocuments = combinedDocs;
                carrierDocuments = carrierStopDocs;
                shipperDocuments = allShipperDocs;
            }
            else {
                allDocuments = await getAllShipperDocumentRequirements(shipment, stopDocumentsConfig);
            }
            if (isCarrier) {
                allDocuments = allDocuments.filter((doc) => !hiddenFromCarrierMap[doc]);
            }
            const shouldCheckToExcludeYardInterchange = carrierDocuments.includes('yard_interchange_ticket');
            const isYardInterchangePresentInShipperAndAccessorialDocs = isCarrier &&
                isCarrierMandatoryStopDocumentsEnabled &&
                (shipperDocuments === null || shipperDocuments === void 0 ? void 0 : shipperDocuments.includes('yard_interchange_ticket'));
            const shouldExcludeYardInterchangeForCarrier = !isFromCarrierDashboard &&
                !isYardInterchangePresentInShipperAndAccessorialDocs &&
                shouldCheckToExcludeYardInterchange;
            setCarrierDocuments(mapDocumentUploadStatus(carrierDocuments, shouldCheckToExcludeYardInterchange));
            setShipperDocuments(mapDocumentUploadStatus(shipperDocuments));
            setDocuments(mapDocumentUploadStatus(allDocuments, shouldExcludeYardInterchangeForCarrier));
            setLoading(false);
        }
        catch (e) {
            showToast(t('ERROR_RETRIEVING_DOCUMENT_REQUIREMENTS'), null, 'error');
            console.error(`Error retrieving document requirements: ${e.message}`);
        }
    }
    async function onUpload(_shipment, _stop, _type, _uploaded, _userVisibility) {
        await getUploadedDocuments();
    }
    const { handleUpload } = useHandleUploadDocuments(shipment, null, onUpload);
    const uploadDocument = async (files, docType) => {
        var _a;
        try {
            (_a = dropzoneRef === null || dropzoneRef === void 0 ? void 0 : dropzoneRef.current) === null || _a === void 0 ? void 0 : _a.open();
            let documentResult;
            documentResult = await handleUpload(files.map((file) => new File([file], docType, Object.assign(Object.assign({}, file.options), { type: file.type }))), docType, undefined, true);
            if (documentResult === null || documentResult === void 0 ? void 0 : documentResult.data) {
                documentResult = await waitDocumentUpload(documentResult.data.key);
            }
            return documentResult;
        }
        catch (e) {
            console.log('error in uploadDocument: ', e);
        }
    };
    async function upload(files, documentType) {
        try {
            if (files.length) {
                const documentResult = await uploadDocument(files, documentType);
                return documentResult;
            }
        }
        catch (e) {
            showToast(t('UPLOAD_FAILED'), null, 'error');
            console.log('error in document upload: ', e);
        }
    }
    const renderRowUploader = (document, isUploading, setDocumentUploading, showIcon = false) => {
        try {
            if (isUploading) {
                return (React.createElement(LoadingCircle, { dataTestId: "LoadingCircle-646dd0ef", text: t('UPLOADING') }));
            }
            return (React.createElement(CargomaticDropzone, { dataTestId: "CargomaticDropzone-f889e38f", dropzoneRef: dropzoneRef, onDrop: async (files) => {
                    setDocumentUploading();
                    await upload(files, document.name);
                    setDocumentUploading();
                }, style: { padding: 0, borderWidth: 0, borderRadius: 0 }, multiple: false, acceptedFiles: ACCEPTED_FILE_TYPES, disabled: isUploading, dropzoneMessage: !showIcon ? t('REPLACE') : '', button: showIcon, showIcon: showIcon }));
        }
        catch (e) {
            console.error(`Error rendering uploader: ${e.message}`);
        }
    };
    function documentIsUploading(name) {
        setDocumentsUploading((currentDocumentsUploading) => {
            if (currentDocumentsUploading[name]) {
                return Object.assign(Object.assign({}, currentDocumentsUploading), { [name]: false });
            }
            return Object.assign(Object.assign({}, currentDocumentsUploading), { [name]: true });
        });
    }
    function renderTitle() {
        return (React.createElement("div", { "data-testid": "div-ae083836", className: "mb-2" },
            React.createElement("h3", { "data-testid": "h3-1a9b6644", className: "text-left" }, t(title))));
    }
    function renderCarrierTitle() {
        return (React.createElement("div", { "data-testid": "div-ae083846", className: "mb-2" },
            React.createElement("h3", { "data-testid": "h3-1a9b6634", className: "text-left" }, t('CARRIER_MANDATORY_DOCUMENTS'))));
    }
    function documentUploader(showIcon, documentToUpload) {
        return renderRowUploader(documentToUpload, documentsUploading[documentToUpload.name], () => documentIsUploading(documentToUpload.name), showIcon);
    }
    function renderContent(loading, requiredDocuments) {
        if (loading) {
            return (React.createElement("div", { "data-testid": "div-a014bb1d" },
                React.createElement(LoadingCircle, { dataTestId: "LoadingCircle-9cfc31f6", text: t('FETCHING_REQUIREMENTS') })));
        }
        return (React.createElement("div", { "data-testid": "div-a188f0ee" },
            !requiredDocuments.length && (React.createElement("p", { "data-testid": "p-8e85c987" }, t('NO_DOCUMENTS_REQUIRED'))),
            requiredDocuments.map((document) => (React.createElement(Document, { document: document, key: document.name, onPreview: () => setDocumentToPreview(document), uploader: (showIcon) => documentUploader(showIcon, document) }))),
            documentToPreview && (React.createElement(UploadedDocumentPreviewModal, { name: documentToPreview.name, url: documentToPreview.url, onHide: () => setDocumentToPreview(null) }))));
    }
    const showInSeperateSections = isCarrierMandatoryStopDocumentsEnabled &&
        !isCarrier &&
        !isFromCarrierDashboard;
    const docs = showInSeperateSections ? shipperDocuments : documents;
    return (React.createElement("div", { "data-testid": "MinimumDocumentRequirements-8hb231ce", className: "lg:col-span-4" },
        React.createElement(CollapsibleItem, { dataTestId: "CollapsibleItem-9db832fc", renderTitle: renderTitle, className: "mb-4", defaultOpen: true }, renderContent(loading, docs)),
        showInSeperateSections && (React.createElement(CollapsibleItem, { dataTestId: "CollapsibleItem-9db823fe", renderTitle: renderCarrierTitle, className: "mb-4", defaultOpen: true }, renderContent(loading, carrierDocuments)))));
}
